import { Check } from "lucide-react";
import type { FC } from "react";

import {
  DOCUMENT_ROLE_LABELS,
  type DocumentRole,
  type FsNode
} from "../../caseFiles/types";
import { nodeTitle } from "../../caseFiles/nodeNames";
import { NewFolderContextItems } from "./NewFolderMenu";
import type { MenuKit } from "./menuKit";
import { canDropInto, type TreeRow } from "./treeRows";

export type CaseFilesMenuModel = {
  menuRow: TreeRow<FsNode> | null;
  menuIds: string[];
  menuNodes: FsNode[];
  menuFiles: FsNode[];
  folders: FsNode[];
  nodes: FsNode[];
  allIncluded: boolean;
  allPrivileged: boolean;
  allNumbered: boolean;
  allCollapsed: boolean;
  menuRole: DocumentRole | null;
  rootName: string;
  /** No rows on screen (an empty or still-loading case). */
  selectAllDisabled: boolean;
};

export type CaseFilesMenuActions = {
  selectAll: () => void;
  openNode: (node: FsNode) => void;
  mention: (node: FsNode) => void;
  unusedAt: (parentId: string | null) => string[];
  createFolder: (parentId: string | null, name?: string) => void;
  upload: (parentId: string | null) => void;
  downloadFolder: (node: FsNode) => void;
  rename: (id: string) => void;
  move: (ids: string[], parentId: string | null) => void;
  downloadItems: (ids: string[]) => void;
  downloadOne: (node: FsNode) => void;
  setInclusion: (ids: string[], included: boolean) => void;
  setRole: (ids: string[], role: DocumentRole) => void;
  classify: (ids: string[]) => void;
  setNumbering: (ids: string[], numbered: boolean) => void;
  deleteNodes: (ids: string[]) => void;
  toggleExpandAll: () => void;
  refresh: () => void;
};

/**
 * Case Files actions shared by the right-click menu and the row's three-dot
 * menu. Only the empty-area menu offers Select All (the list on screen, the
 * same set the header menu uses); a file's own menu acts on that file.
 */
export const CaseFilesMenuItems: FC<{
  kit: MenuKit;
  model: CaseFilesMenuModel;
  actions: CaseFilesMenuActions;
}> = ({ kit, model, actions }) => {
  const { Item, Separator, Sub, SubTrigger, SubContent } = kit;
  const {
    menuRow,
    menuIds,
    menuNodes,
    menuFiles,
    folders,
    nodes,
    allIncluded,
    allPrivileged,
    allNumbered,
    allCollapsed,
    menuRole,
    rootName,
    selectAllDisabled
  } = model;

  const selectAll = (
    <Item
      onSelect={actions.selectAll}
      disabled={selectAllDisabled}
      data-testid="file-menu-select-all"
    >
      Select All
    </Item>
  );

  if (!menuRow) {
    return (
      <>
        {selectAll}
        <Separator />
        <NewFolderContextItems
          kit={kit}
          label="New folder"
          names={actions.unusedAt(null)}
          onCreateBlank={() => actions.createFolder(null)}
          onCreateCategory={(name) => actions.createFolder(null, name)}
        />
        <Item onSelect={() => actions.upload(null)}>Upload files</Item>
        {folders.length ? (
          <Item onSelect={actions.toggleExpandAll}>
            {allCollapsed ? "Expand all" : "Collapse all"}
          </Item>
        ) : null}
        <Item onSelect={actions.refresh}>Refresh</Item>
      </>
    );
  }

  return (
    <>
      <Item onSelect={() => actions.openNode(menuRow.node)}>
        {menuRow.node.kind === "folder"
          ? menuRow.expanded
            ? "Collapse"
            : "Expand"
          : "Open"}
      </Item>
      <Item onSelect={() => actions.mention(menuRow.node)}>
        Mention in chat
      </Item>
      <Separator />
      {menuRow.node.kind === "folder" ? (
        <>
          <NewFolderContextItems
            kit={kit}
            label="New folder inside"
            names={actions.unusedAt(menuRow.node.id)}
            onCreateBlank={() => actions.createFolder(menuRow.node.id)}
            onCreateCategory={(name) =>
              actions.createFolder(menuRow.node.id, name)
            }
          />
          <Item onSelect={() => actions.upload(menuRow.node.id)}>
            Upload here
          </Item>
          {menuIds.length === 1 ? (
            <Item onSelect={() => actions.downloadFolder(menuRow.node)}>
              Download Folder
            </Item>
          ) : null}
        </>
      ) : null}
      <Item
        disabled={menuIds.length !== 1}
        onSelect={() => actions.rename(menuRow.node.id)}
      >
        Rename
      </Item>
      <Sub>
        <SubTrigger>Move to</SubTrigger>
        <SubContent className="max-h-72 w-60 overflow-y-auto">
          <Item
            disabled={!canDropInto(nodes, menuIds, null)}
            onSelect={() => actions.move(menuIds, null)}
          >
            {rootName}
          </Item>
          {folders
            .filter(
              (f) =>
                canDropInto(nodes, menuIds, f.id) &&
                !menuNodes.every((n) => n.parent_id === f.id)
            )
            .map((f) => (
              <Item key={f.id} onSelect={() => actions.move(menuIds, f.id)}>
                <span className="truncate">
                  {f.path_cache.replace(/^\//, "") || f.name}
                </span>
              </Item>
            ))}
        </SubContent>
      </Sub>
      {menuIds.length > 1 ? (
        <Item onSelect={() => actions.downloadItems(menuIds)}>
          Download {menuIds.length} Items
        </Item>
      ) : menuRow.node.kind === "file" ? (
        <Item
          disabled={!menuRow.node.storage_path}
          onSelect={() => actions.downloadOne(menuRow.node)}
        >
          Download
        </Item>
      ) : null}
      <Separator />
      <Item
        disabled={!allIncluded && allPrivileged}
        title={
          !allIncluded && allPrivileged
            ? "Internal files never go in the packet. Change the role first."
            : undefined
        }
        onSelect={() => actions.setInclusion(menuIds, !allIncluded)}
      >
        {allIncluded ? "Exclude from Packet" : "Include in Packet"}
        {!allIncluded && allPrivileged ? (
          <span className="ml-auto pl-3 text-sm text-fg-4">Internal</span>
        ) : null}
      </Item>
      {menuFiles.length ? (
        <Sub>
          <SubTrigger>Document Role</SubTrigger>
          <SubContent className="w-64">
            {(Object.keys(DOCUMENT_ROLE_LABELS) as DocumentRole[]).map(
              (role) => (
                <Item
                  key={role}
                  onSelect={() => actions.setRole(menuIds, role)}
                >
                  <span className="flex-1">{DOCUMENT_ROLE_LABELS[role]}</span>
                  {menuRole === role ? (
                    <Check className="size-3.5 text-fg-3" aria-hidden />
                  ) : null}
                </Item>
              )
            )}
            <Separator />
            <p className="px-2 py-1.5 text-sm text-fg-3">
              Internal files are the firm's privileged work. Agents read them
              for facts but can't cite, quote or file them.
            </p>
          </SubContent>
        </Sub>
      ) : null}
      {menuFiles.length ? (
        <Item onSelect={() => actions.classify(menuFiles.map((n) => n.id))}>
          Category and Type…
        </Item>
      ) : null}
      {menuNodes.some((n) => n.kind === "folder") ? (
        <Item onSelect={() => actions.setNumbering(menuIds, !allNumbered)}>
          {allNumbered ? "Make heading (unnumbered)" : "Number as exhibit"}
        </Item>
      ) : null}
      <Separator />
      <Item variant="destructive" onSelect={() => actions.deleteNodes(menuIds)}>
        Delete{menuIds.length > 1 ? ` ${menuIds.length} items` : ""}
      </Item>
    </>
  );
};
